import { useEffect, useRef } from 'react';

type UseInitialSuggestionsPollingOptions = {
  suggestionIds: number[] | undefined;
  isDisabled: boolean;
  pollSuggestion: (suggestionId: number) => void;
};

/**
 * Kicks off polling for an initial set of suggestion ids exactly once per load
 * cycle — specifically, on the first render after `isDisabled` transitions
 * from true to false. Subsequent changes to `suggestionIds` do not re-trigger
 * polling; the action handlers that mutate those ids (reprocess/create)
 * dispatch their own poll for the affected suggestion.
 *
 * Reacting to every list change would start a second concurrent poll sharing
 * the same reduxPoll timer id as the first, cancelling it and leaving any
 * caller awaiting the first promise (e.g. exportClip) hanging indefinitely.
 */
export default function useInitialSuggestionsPolling({
  suggestionIds,
  isDisabled,
  pollSuggestion,
}: UseInitialSuggestionsPollingOptions) {
  const hasInitiatedPollingRef = useRef(false);

  useEffect(() => {
    if (hasInitiatedPollingRef.current || isDisabled) {
      return;
    }

    hasInitiatedPollingRef.current = true;

    suggestionIds?.forEach(pollSuggestion);
  }, [isDisabled, pollSuggestion, suggestionIds]);
}
